import React from 'react';

/**
 * SessionTimeoutModal
 *
 * Displayed when the user has been idle for 58 minutes (2 minutes before auto-logout).
 * Shows a live countdown and two actions: stay logged in or log out immediately.
 *
 * Props:
 *  secondsLeft  – number of seconds remaining before auto-logout
 *  onStay       – called when the user clicks "Stay Logged In" (resets the timer)
 *  onLogout     – called when the user clicks "Log Out Now"
 */
export default function SessionTimeoutModal({ secondsLeft, onStay, onLogout }) {
  const mins    = Math.floor(secondsLeft / 60);
  const secs    = secondsLeft % 60;
  const timeStr = mins > 0
    ? `${mins}:${String(secs).padStart(2, '0')}`
    : `${secs}s`;

  // Progress: 120 → 0  →  arc sweeps from full to empty
  const total       = 120; // WARNING_BEFORE_MS / 1000
  const progress    = Math.max(0, secondsLeft / total); // 1 → 0
  const radius      = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDash  = circumference * progress;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Card */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="timeout-title"
        aria-describedby="timeout-desc"
      >
        {/* Animated countdown ring */}
        <div className="mx-auto mb-5 relative" style={{ width: 88, height: 88 }}>
          <svg width="88" height="88" viewBox="0 0 88 88" className="absolute inset-0 -rotate-90">
            {/* Track */}
            <circle
              cx="44" cy="44" r={radius}
              fill="none"
              stroke="#fef3c7"
              strokeWidth="6"
            />
            {/* Progress arc */}
            <circle
              cx="44" cy="44" r={radius}
              fill="none"
              stroke="#f59e0b"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${strokeDash} ${circumference}`}
              style={{ transition: 'stroke-dasharray 0.9s linear' }}
            />
          </svg>

          {/* Warning icon centered */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="h-8 w-8 text-amber-500"
              viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>

        <h2 id="timeout-title" className="text-lg font-bold text-gray-900 mb-2">
          Session Expiring Soon
        </h2>
        <p id="timeout-desc" className="text-gray-500 text-sm mb-1">
          You've been inactive. You'll be automatically signed out in:
        </p>

        {/* Countdown */}
        <div className="text-4xl font-extrabold tabular-nums my-4" style={{ color: '#f59e0b' }}>
          {timeStr}
        </div>

        <p className="text-gray-400 text-xs mb-6">
          Click <strong className="text-gray-600">Stay Logged In</strong> to continue working.
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onLogout}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            Log Out
          </button>
          <button
            onClick={onStay}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}
